import axios from "axios";
import { create } from "zustand";

interface ITimeseriesPoint {
  bucket: string;
  count: number;
}

interface ISummaryPoint {
  date: string;
  appOpens: number;
  uniqueUsers: number;
  sessionsCount: number;
}

interface IScreenStat {
  screen: string;
  count: number;
  uniqueUsers: number;
}

interface IErrorStat {
  message: string;
  screen?: string;
  count: number;
  lastSeen: string;
}

export interface IUserAnalyticsSummary {
  userId: string | null;
  email?: string;
  firstName?: string;
  lastName?: string;
  app_open: number;
  login_attempt: number;
  login_success: number;
  login_failed: number;
  logout: number;
  popup_open: number;
  bottom_tab_click: number;
  manual_drop_detail_open: number;
  tomra_drop_detail_open: number;
  beevision_drop_detail_open: number;
  store_change: number;
  impact_screen_open: number;
  lottery_list_open: number;
  lottery_join_success: number;
  app_error: number;
  home_button_click: number;
  screen_view: number;
}

export interface IEventUserBreakdown {
  userId: string | null;
  email?: string;
  firstName?: string;
  lastName?: string;
  count: number;
  lastSeen: string;
  timestamps: string[];
}

export interface IEventCounts {
  app_open: number;
  login_attempt: number;
  login_success: number;
  login_failed: number;
  logout: number;
  popup_open: number;
  notifications_sheet_open: number;
  bottom_tab_click: number;
  manual_drop_detail_open: number;
  tomra_drop_detail_open: number;
  beevision_drop_detail_open: number;
  store_change: number;
  impact_screen_open: number;
  lottery_list_open: number;
  lottery_join_success: number;
  app_error: number;
  home_button_click: number;
  review_prompt_requested: number;
  push_notification_opened: number;
}

export interface IReviewPrompt {
  eventId: string;
  timestamp: string;
  platform?: string;
  appVersion?: string;
  dropCount?: number;
  dropType?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

const defaultEventCounts: IEventCounts = {
  app_open: 0,
  login_attempt: 0,
  login_success: 0,
  login_failed: 0,
  logout: 0,
  popup_open: 0,
  notifications_sheet_open: 0,
  bottom_tab_click: 0,
  manual_drop_detail_open: 0,
  tomra_drop_detail_open: 0,
  beevision_drop_detail_open: 0,
  store_change: 0,
  impact_screen_open: 0,
  lottery_list_open: 0,
  lottery_join_success: 0,
  app_error: 0,
  home_button_click: 0,
  review_prompt_requested: 0,
  push_notification_opened: 0,
};

interface IAnalyticsStore {
  summary: ISummaryPoint[];
  screens: IScreenStat[];
  timeseries: ITimeseriesPoint[];
  errors: IErrorStat[];
  eventCounts: IEventCounts;
  users: IUserAnalyticsSummary[];
  reviewPrompts: IReviewPrompt[];
  /**
   * One flag per panel rather than one for the page. The endpoints have very
   * different response times - the per-user summary is by far the slowest - so
   * a single flag meant the whole screen waited on the slowest request. Each
   * panel now renders as soon as its own data lands and shows a skeleton until
   * then.
   */
  loadingSummary: boolean;
  loadingScreens: boolean;
  loadingErrors: boolean;
  loadingEventCounts: boolean;
  loadingUsers: boolean;
  loadingReviewPrompts: boolean;
  loadSummary: (params: { from?: string; to?: string }) => Promise<void>;
  loadScreens: (params: { from?: string; to?: string }) => Promise<void>;
  loadTimeseries: (params: {
    event: string;
    from?: string;
    to?: string;
    period?: "daily" | "weekly";
  }) => Promise<void>;
  loadErrors: (params: { from?: string; to?: string }) => Promise<void>;
  loadEventCounts: (params: { from?: string; to?: string }) => Promise<void>;
  loadUserSummary: (params: { from?: string; to?: string }) => Promise<void>;
  loadEventUserBreakdown: (params: {
    eventName: string;
    from?: string;
    to?: string;
  }) => Promise<IEventUserBreakdown[]>;
  loadReviewPrompts: (params: { from?: string; to?: string }) => Promise<void>;
}

export const useAnalytics = create<IAnalyticsStore>((set) => {
  return {
  summary: [],
  screens: [],
  timeseries: [],
  errors: [],
  eventCounts: defaultEventCounts,
  users: [],
  reviewPrompts: [],
  // Start true. The screen fetches all six on mount, but the effects only run
  // after the first paint - starting false meant that paint rendered the empty
  // state ("No screen data for this period") for a frame before the skeletons
  // replaced it.
  loadingSummary: true,
  loadingScreens: true,
  loadingErrors: true,
  loadingEventCounts: true,
  loadingUsers: true,
  loadingReviewPrompts: true,
  loadSummary: async ({ from, to }) => {
    set({ loadingSummary: true });
    try {
      const res = await axios
        .get("/analytics/summary", {
          params: { from, to },
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error("[Analytics] Failed to load summary", error);
          return undefined;
        });

      const data = res && (res as any).data && (res as any).data.data;
      set({ summary: Array.isArray(data) ? data : [] });
    } finally {
      set({ loadingSummary: false });
    }
  },
  loadScreens: async ({ from, to }) => {
    set({ loadingScreens: true });
    try {
      const res = await axios
        .get("/analytics/screens", {
          params: { from, to },
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error("[Analytics] Failed to load screens", error);
          return undefined;
        });
      const data = res && (res as any).data && (res as any).data.data;
      set({ screens: Array.isArray(data) ? data : [] });
    } finally {
      set({ loadingScreens: false });
    }
  },
  // Not rendered on the analytics screen, so it has no panel flag of its own.
  loadTimeseries: async ({ event, from, to, period }) => {
    const res = await axios.get("/analytics/events/timeseries", {
      params: { event, from, to, period },
    });
    set({ timeseries: res.data?.data ?? [] });
  },
  loadErrors: async ({ from, to }) => {
    set({ loadingErrors: true });
    try {
      const res = await axios
        .get("/analytics/errors", {
          params: { from, to },
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error("[Analytics] Failed to load errors", error);
          return undefined;
        });
      const data = res && (res as any).data && (res as any).data.data;
      set({ errors: Array.isArray(data) ? data : [] });
    } finally {
      set({ loadingErrors: false });
    }
  },
  loadEventCounts: async ({ from, to }) => {
    set({ loadingEventCounts: true });
    try {
      const res = await axios
        .get("/analytics/event-counts", {
          params: { from, to },
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error("[Analytics] Failed to load event counts", error);
          return undefined;
        });
      const counts = res && (res as any).data && (res as any).data.data;
      if (counts) {
        set({ eventCounts: { ...defaultEventCounts, ...counts } });
      }
    } catch {
      // keep existing zeros on error
    } finally {
      set({ loadingEventCounts: false });
    }
  },
  loadUserSummary: async ({ from, to }) => {
    set({ loadingUsers: true });
    try {
      const res = await axios
        .get("/analytics/users/summary", {
          params: { from, to },
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error("[Analytics] Failed to load user summary", error);
          return undefined;
        });
      const data = res && (res as any).data && (res as any).data.data;
      set({ users: Array.isArray(data) ? data : [] });
    } catch {
      // leave previous users state on error
    } finally {
      set({ loadingUsers: false });
    }
  },
  loadEventUserBreakdown: async ({ eventName, from, to }) => {
    try {
      const res = await axios.get(
        `/analytics/events/${eventName}/by-user`,
        {
          params: { from, to },
        }
      );
      const data = res?.data?.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        `[Analytics] Failed to load breakdown for ${eventName}`,
        error
      );
      return [];
    }
  },
  loadReviewPrompts: async ({ from, to }) => {
    set({ loadingReviewPrompts: true });
    try {
      const res = await axios
        .get("/analytics/review-prompts", { params: { from, to } })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error("[Analytics] Failed to load review prompts", error);
          return undefined;
        });
      const data = res && (res as any).data && (res as any).data.data;
      set({ reviewPrompts: Array.isArray(data) ? data : [] });
    } catch {
      // keep previous state on error
    } finally {
      set({ loadingReviewPrompts: false });
    }
  },
}});

